import DOMPurify from "dompurify";
import { ArrowLeft, CheckCircle2, ChevronDown, ChevronUp, Copy, ExternalLink, FileText, Image, Languages, Mail, ShieldCheck, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";

export type MessageDetail = {
  id: string;
  direction: string;
  status: string;
  from_address: string;
  to_addresses: string;
  cc_addresses: string;
  bcc_addresses: string;
  subject: string;
  preview: string;
  text_body: string | null;
  html_body: string | null;
  verification_code: string | null;
  message_id: string | null;
  in_reply_to: string | null;
  references_header: string | null;
  size: number;
  expires_at: string | null;
  received_at: string | null;
  sent_at: string | null;
  created_at: string;
};

export type Attachment = {
  id: string;
  name: string;
  mime_type: string;
  size: number;
  content_id: string | null;
  disposition: string | null;
};

export type MessagePayload = {
  message: MessageDetail;
  attachments: Attachment[];
  headers: Array<{ name: string; value: string }>;
  forwardAttempts: Array<{ destination: string; status: string; error: string | null; created_at: string }>;
};

type SafeMail = { document: string; links: Array<{ label: string; url: string }>; blockedImages: number };

function list(value: string): string[] {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function bytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 ** 2).toFixed(1)} MB`;
}

function date(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function safeLink(value: string): string | null {
  try {
    const url = new URL(value);
    return ["http:", "https:", "mailto:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function renderMail(html: string, loadImages: boolean): SafeMail {
  const clean = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button", "link", "meta", "base"],
    FORBID_ATTR: ["srcdoc"],
  });
  const page = new DOMParser().parseFromString(clean, "text/html");
  const links: Array<{ label: string; url: string }> = [];
  let blockedImages = 0;

  page.querySelectorAll("a").forEach((anchor) => {
    const url = safeLink(anchor.getAttribute("href") || "");
    if (url) links.push({ label: anchor.textContent?.trim() || url, url });
    anchor.removeAttribute("href");
    anchor.removeAttribute("target");
    anchor.removeAttribute("rel");
    anchor.classList.add("mail-link-text");
  });

  page.querySelectorAll("img").forEach((image) => {
    const source = image.getAttribute("src") || "";
    image.removeAttribute("srcset");
    if (/^https?:\/\//iu.test(source)) {
      if (!loadImages) {
        blockedImages += 1;
        const hidden = page.createElement("span");
        hidden.className = "mail-image-hidden";
        hidden.textContent = image.getAttribute("alt") || "远程图片已隐藏";
        image.replaceWith(hidden);
      }
      return;
    }
    if (!source.startsWith("data:image/")) {
      const hidden = page.createElement("span");
      hidden.className = "mail-image-hidden";
      hidden.textContent = image.getAttribute("alt") || "内嵌图片已隐藏";
      image.replaceWith(hidden);
    }
  });

  page.querySelectorAll<HTMLElement>("[style]").forEach((element) => {
    element.setAttribute("style", (element.getAttribute("style") || "").replace(/url\s*\([^)]*\)|@import[^;]+;?/giu, ""));
  });
  page.querySelectorAll("style").forEach((style) => {
    style.textContent = (style.textContent || "").replace(/url\s*\([^)]*\)|@import[^;]+;?/giu, "");
  });

  const policy = loadImages
    ? "default-src 'none'; img-src data: blob: https: http:; style-src 'unsafe-inline'; font-src data:; form-action 'none'; base-uri 'none'"
    : "default-src 'none'; img-src data: blob:; style-src 'unsafe-inline'; font-src data:; form-action 'none'; base-uri 'none'";
  const frameStyle = "html{color:#202023;background:#fff}body{margin:0;padding:24px;font:14px/1.65 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;overflow-wrap:anywhere}img{max-width:100%;height:auto}.mail-link-text{color:#1769aa;text-decoration:underline}.mail-image-hidden{display:inline-block;padding:8px 10px;margin:4px 0;color:#666;background:#f3f3f4;border:1px solid #ddd;border-radius:4px;font-size:12px}blockquote{margin-left:0;padding-left:14px;border-left:3px solid #ddd;color:#555}table{max-width:100%}";
  const document = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${policy}"><meta name="referrer" content="no-referrer"><style>${frameStyle}</style></head><body>${page.body.innerHTML}</body></html>`;
  const uniqueLinks = links.filter((link, index) => links.findIndex((item) => item.url === link.url) === index);
  return { document, links: uniqueLinks, blockedImages };
}

export function MessageView({ data, onBack, onDelete, onTranslated }: {
  data: MessagePayload;
  onBack: () => void;
  onDelete: () => void;
  onTranslated: (text: string) => void;
}) {
  const [view, setView] = useState<"html" | "text">(data.message.html_body ? "html" : "text");
  const [loadImages, setLoadImages] = useState(false);
  const [showHeaders, setShowHeaders] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rendered = useMemo(() => renderMail(data.message.html_body || "", loadImages), [data.message.html_body, loadImages]);
  const replyTo = data.headers.find((header) => header.name.toLowerCase() === "reply-to")?.value;

  async function translate() {
    setBusy(true);
    try {
      const response = await fetch(`/api/messages/${data.message.id}/translate`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language: "简体中文" }),
      });
      const result = await response.json() as { text_body?: string; error?: string };
      if (!response.ok || !result.text_body) throw new Error(result.error || "翻译失败");
      onTranslated(result.text_body);
      setView("text");
    } finally {
      setBusy(false);
    }
  }

  function openLink() {
    if (!link) return;
    window.open(link, "_blank", "noopener,noreferrer");
    setLink(null);
  }

  return (
    <div className="message-page">
      <div className="message-page-bar">
        <button className="button secondary" onClick={onBack}><ArrowLeft size={16} />返回邮件</button>
        <div className="message-page-actions">
          <button className="button secondary" disabled={busy} onClick={() => void translate()}><Languages size={16} />{busy ? "翻译中" : "翻译"}</button>
          <button className="button danger-button" onClick={onDelete}><Trash2 size={16} />删除</button>
        </div>
      </div>

      <section className="message-document">
        <div className="message-title-row">
          <div><span className="eyebrow">邮件详情</span><h1>{data.message.subject || "无主题"}</h1></div>
          <span className={`status-chip ${data.message.status}`}>{data.message.status}</span>
        </div>

        <div className="message-address-grid">
          <div className="sender-card"><div className="avatar">{data.message.from_address[0]?.toUpperCase()}</div><div><span>发件人</span><strong>{data.message.from_address}</strong></div></div>
          <dl>
            <div><dt>收件人</dt><dd>{list(data.message.to_addresses).join(", ") || "—"}</dd></div>
            <div><dt>抄送</dt><dd>{list(data.message.cc_addresses).join(", ") || "—"}</dd></div>
            <div><dt>密送</dt><dd>{list(data.message.bcc_addresses).join(", ") || "—"}</dd></div>
            <div><dt>回复到</dt><dd>{replyTo || "—"}</dd></div>
            <div><dt>时间</dt><dd>{date(data.message.received_at || data.message.sent_at || data.message.created_at)}</dd></div>
          </dl>
        </div>

        {data.message.verification_code && <div className="code-box"><span>验证码</span><strong>{data.message.verification_code}</strong><button onClick={() => void navigator.clipboard.writeText(data.message.verification_code || "")}><Copy size={15} />复制</button></div>}

        <div className="mail-toolbar">
          <div className="segmented mail-tabs">
            <button className={view === "html" ? "active" : ""} disabled={!data.message.html_body} onClick={() => setView("html")}>邮件内容</button>
            <button className={view === "text" ? "active" : ""} onClick={() => setView("text")}>纯文本</button>
          </div>
          {view === "html" && <button className="button secondary" disabled={loadImages || rendered.blockedImages === 0} onClick={() => setLoadImages(true)}><Image size={16} />{loadImages ? "图片已加载" : `加载图片${rendered.blockedImages ? ` (${rendered.blockedImages})` : ""}`}</button>}
        </div>

        <div className="mail-safety"><ShieldCheck size={16} /><span>外部内容默认关闭，链接打开前需要确认。</span></div>
        {view === "html" ? <iframe className="mail-frame" title="邮件内容" sandbox="" referrerPolicy="no-referrer" srcDoc={rendered.document} /> : <pre className="mail-text">{data.message.text_body || "此邮件没有纯文本正文。"}</pre>}

        {rendered.links.length > 0 && <section className="message-section"><h2><ExternalLink size={17} />邮件链接</h2><div className="mail-links">{rendered.links.map((item) => <button key={item.url} onClick={() => setLink(item.url)}><span>{item.label}</span><small>{item.url}</small></button>)}</div></section>}

        {data.attachments.length > 0 && <section className="message-section"><h2><Mail size={17} />附件</h2><div className="attachment-grid">{data.attachments.map((item) => <a key={item.id} href={`/api/attachments/${item.id}`}><span><FileText size={16} />{item.name}</span><small>{bytes(item.size)}</small></a>)}</div></section>}

        {data.forwardAttempts.length > 0 && <section className="message-section"><h2><CheckCircle2 size={17} />转发记录</h2><div className="forward-list">{data.forwardAttempts.map((item, index) => <div key={`${item.destination}-${index}`}><strong>{item.destination}</strong><span>{item.status}</span><time>{date(item.created_at)}</time>{item.error && <small>{item.error}</small>}</div>)}</div></section>}

        <section className="message-section headers-section">
          <button className="headers-toggle" onClick={() => setShowHeaders((value) => !value)}><span><FileText size={17} />邮件头 ({data.headers.length})</span>{showHeaders ? <ChevronUp size={17} /> : <ChevronDown size={17} />}</button>
          {showHeaders && <div className="headers-table">{data.headers.length ? data.headers.map((header, index) => <div key={`${header.name}-${index}`}><strong>{header.name}</strong><span>{header.value}</span></div>) : <p>没有可用的邮件头。</p>}</div>}
        </section>
      </section>

      {link && <div className="modal-layer"><button className="modal-backdrop" aria-label="关闭" onClick={() => setLink(null)} /><section className="modal link-confirm"><div className="modal-head"><h2>打开外部链接</h2><button className="icon-button" onClick={() => setLink(null)}><X size={18} /></button></div><div className="form"><p>此链接来自邮件，请确认地址。</p><code>{link}</code><div className="form-actions"><button className="button secondary" onClick={() => setLink(null)}>取消</button><button className="button primary" onClick={openLink}><ExternalLink size={16} />继续打开</button></div></div></section></div>}
    </div>
  );
}
